import { MainLogo } from "modules/main/assets/main_logo"
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useEffect, useRef, useState } from "react";
import { cartCreationHealth, cartCreationStream } from "modules/main/services/endpoints";
import { AutomatizationPushMessage, AutomatizationStatus, AutomatizationStep, CartCreationHealthResponse } from "../types/cartAutomatizationStatus";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { StoreData } from "modules/main/types/storeData";
import { getStoreBdData } from "utils/storeDataManager";
import {
    Item,
    ItemActions,
    ItemContent,
    ItemDescription,
    ItemMedia,
    ItemTitle,
} from "@/components/ui/item"
import { RotateCcw, TriangleAlert, WifiOff } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Spinner } from "@/components/ui/spinner";
import apiClient from "modules/main/services/apiClient";

const SILENCE_THRESHOLD_MS = 60_000;

interface CartCreationStreamStatusProps extends React.HTMLAttributes<HTMLDivElement>{
    setStatus:(status:AutomatizationStep[])=>void    
    status:AutomatizationStep[]
    setOpenMissedItemsDialog: (open: boolean) => void;
    setRefreshTik: React.Dispatch<React.SetStateAction<number>>;
    storeData:StoreData []
    resumeErrorsByStore: Record<number, string>;
    onRetryResume: (storeId: number) => Promise<void> | void;
    resumingStores: Set<number>;
    /** Called when the user wants to restart the entire cart creation process from scratch. */
    onRetryProcess?: () => Promise<void>;
    /** Called when a STREAMING_TTL_UPDATE event arrives so RemoteBrowser can sync its countdown. */
    onStreamingTtlUpdate?: (update: { store_id: number; newExpiresAt: string; remainingSeconds: number }) => void;
}

const GLOBAL_STATUS_TEXT: Record<AutomatizationStatus["status"], string> = {
    PENDING:"Preparando todo...",
    STARTED: "Armando tus carritos...",
    WAITING: "Necesitamos tu ayuda",
    COMPLETED: "¡Tus carritos están listos!",
    FAILED: "Ocurrió un error inesperado",
};

export const CartCreationStreamStatus = ({setStatus, setOpenMissedItemsDialog, setRefreshTik, onRetryProcess, onStreamingTtlUpdate, ...props}:CartCreationStreamStatusProps) => {
    
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);    
    const abortControllerRef = useRef<AbortController | null>(null);
    const [message, setMessage] = useState<string>("");
    const statusRef = useRef<AutomatizationStep[]>(props.status);
    /**
     * Tracks the last time an SSE message was received.
     * Starts as null — only set to a Date once the SSE connection opens (onopen).
     * This prevents false positives during the initial connection phase.
     */
    const lastMessageAtRef = useRef<Date | null>(null);
    const [connectionWarning, setConnectionWarning] = useState<string | null>(null);
    const [isRetryingProcess, setIsRetryingProcess] = useState<boolean>(false);

    useEffect(() => {
        statusRef.current = props.status;
    }, [props.status]);

    useEffect(() => {  
        startStreamingCreation();
        return () => {
            disconnectStream();
        };
    },[]);

    /**
     * Silence handler: polls /health when the SSE has been quiet for over 60 seconds.
     * Guards:
     *   - `lastMessageAtRef.current === null` — SSE not yet connected.
     *   - All active stores are awaiting user interaction (LOGIN_REQUIRED / ADDRESS_NOT_SELECTED) —
     *     silence is expected in this state because carri-browser only publishes when the step
     *     progresses, not while waiting for the user. Triggering a health check here would
     *     show a spurious "Verificando conexión..." banner while the user is in the remote browser.
     */
    useEffect(() => {
        const AWAITING_USER_STATUSES = new Set<AutomatizationStep["status"]>(["LOGIN_REQUIRED", "ADDRESS_NOT_SELECTED"]);
        const interval = setInterval(async () => {
            if (lastMessageAtRef.current === null) return;
            if (Date.now() - lastMessageAtRef.current.getTime() <= SILENCE_THRESHOLD_MS) return;

            // Skip if every non-terminal store is intentionally waiting for user interaction.
            const activeStores = statusRef.current.filter(
                s => s.status !== "COMPLETED" && s.status !== "FAILED" && s.status !== "PARTIAL"
            );
            if (activeStores.length > 0 && activeStores.every(s => AWAITING_USER_STATUSES.has(s.status))) return;

            setConnectionWarning("Verificando conexión...");
            try {
                const health = await apiClient.get<CartCreationHealthResponse>(cartCreationHealth);
                if (health.status === null || health.status === "FAILED") {
                    setConnectionWarning("El proceso fue interrumpido inesperadamente");
                } else {
                    setConnectionWarning(null);
                }
            } catch {
                setConnectionWarning("El proceso fue interrumpido inesperadamente");
            }
        }, SILENCE_THRESHOLD_MS);
        return () => clearInterval(interval);
    }, []);

    const getGlobalStatusText = (status: AutomatizationStatus["status"], fallback?: string) => {
        return GLOBAL_STATUS_TEXT[status] ?? fallback ?? "Actualizando estado de automatizacion...";
    };

    /**
     * Patches only the message field of an existing step without replacing its full state.
     * Used by watchdog events (RETRY_ATTEMPT, TIMEOUT_WARNING, STEP_TIMEOUT_CANCELLED).
     */
    const patchStepMessage = (store_id: number, msg: string) => {
        const currentStatus = statusRef.current ?? [];
        const idx = currentStatus.findIndex(s => s.store_id === store_id);
        if (idx === -1) return;
        const updated = [...currentStatus];
        updated[idx] = { ...updated[idx], message: msg };
        statusRef.current = updated;
        setStatus(updated);
    };

    const proccessMessage = (message: AutomatizationPushMessage) => {
        if (message.type === "GLOBAL" && message.globalStatus) {
            setMessage(getGlobalStatusText(message.globalStatus.status, message.globalStatus.message));
            return;
        }

        // Watchdog: entire process cancelled by global TTL
        if (message.type === "PROCESS_CANCELLED") {
            setMessage("El proceso fue cancelado por tiempo de espera");
            return;
        }

        // Watchdog: store-level annotations — patch message without replacing the full step state
        if (message.type === "RETRY_ATTEMPT") {
            patchStepMessage(message.stepStatus.store_id, `Reintentando... (intento ${message.stepStatus.attemptNumber} de ${message.stepStatus.maxAttempts})`);
            return;
        }
        if (message.type === "TIMEOUT_WARNING") {
            patchStepMessage(message.stepStatus.store_id, "Demora detectada, verificando...");
            return;
        }
        if (message.type === "STEP_TIMEOUT_CANCELLED") {
            patchStepMessage(message.stepStatus.store_id, "Tiempo de espera agotado");
            return;
        }
        if (message.type === "STREAMING_TTL_UPDATE") {
            onStreamingTtlUpdate?.(message.stepStatus);
            return;
        }

        if (setStatus && message.stepStatus) {
            const currentStatus = statusRef.current ?? [];
            const idx = currentStatus.findIndex(s => s.store_id === message.stepStatus!.store_id);
            let updated: AutomatizationStep[];
            if (idx !== -1) {
                updated = [...currentStatus];
                updated[idx] = message.stepStatus!;
            } else {
                updated = [...currentStatus, message.stepStatus!];
            }
            statusRef.current = updated;
            setStatus(updated);
        }

    }

    const startStreamingCreation = async () => {
        disconnectStream();

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            await fetchEventSource(cartCreationStream, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                openWhenHidden: true,
                signal: controller.signal,

                async onopen(response) {
                    if (!response.ok || response.status !== 200) {
                        throw new Error("Failed to connect to chat stream");
                    }
                    setIsConnected(true);
                    setError(null);
                    // Start the silence timer from the moment the SSE connection is established
                    lastMessageAtRef.current = new Date();
                    return;
                },

                onmessage(event) {
                    if(!event.data) return;
                    lastMessageAtRef.current = new Date();
                    setConnectionWarning(null);
                    try {
                        const parsedData = JSON.parse(event.data);
                        
                        if (parsedData.message === "PRODUCTS_UPDATED") {
                            setRefreshTik(prev => prev + 1);
                            return;
                        }

                        proccessMessage(parsedData as AutomatizationPushMessage);
                    } catch (err) {
                        console.error("Error parsing message:", err);
                        return;
                    }
                },

                onclose() {
                    setIsConnected(false);
                },

                onerror(err) {
                    setError("Error in chat stream");
                    disconnectStream();
                    setIsConnected(false);
                }
            });
            

        }catch (err) {
            console.error("Error in chat stream:", err);
        }
    }    


    const disconnectStream = () => {
        if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
        }
        setIsConnected(false);
    };

    const isPartialShow = (steps: AutomatizationStep[]) => {
        if (!steps || !Array.isArray(steps)) return false;
        return steps.some(step => step.status === "PARTIAL");
    }

    const showPartialItem = isPartialShow(props.status);
    const hasResumeErrors = Object.keys(props.resumeErrorsByStore).length > 0;

    // Cuenta productos que no pudieron agregarse al carrito por tienda.
    const getNonStockProductsCount = (steps: AutomatizationStep[]): number => {
        if (!steps || !Array.isArray(steps)) return 0;

        return steps.reduce((total, step) => {
            const requested = step.results?.totalItemsRequested ?? 0;
            const inCart = step.results?.totalItemsInCart ?? 0;
            const missing = Math.max(requested - inCart, 0);
            return total + missing;
        }, 0);
    }

    return(
    <div className={cn("flex flex-col justify-center gap-1", props.className)}>
        <MainLogo classname="h-16 mx-auto"/>
        <h2 className="text-lg font-semibold text-center text-muted-foreground animate-pulse">{message==="" ? "Preparando Todo..." : message}</h2>
        <AnimatePresence initial={false}>
        {connectionWarning && (
        <motion.div
            key="connection-warning"
            layout
            initial={{ opacity: 0, height: 0, y: -8 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -8 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            className="w-full overflow-hidden"
        >
        <div className="w-full flex flex-col gap-2 justify-center items-center text-xs">
            <Item variant="default" className="bg-yellow-50 border border-yellow-200">
                <ItemMedia variant="icon">
                    <WifiOff className="text-yellow-700" />
                </ItemMedia>
                <ItemContent>
                    <ItemTitle className="text-xs text-yellow-900">{connectionWarning}</ItemTitle>
                </ItemContent>
                {onRetryProcess && connectionWarning !== "Verificando conexión..." && (
                <ItemActions>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={isRetryingProcess}
                        onClick={async () => {
                            setIsRetryingProcess(true);
                            lastMessageAtRef.current = null;
                            setConnectionWarning(null);
                            try {
                                await onRetryProcess();
                            } catch {
                                setConnectionWarning("No se pudo reiniciar el proceso");
                            } finally {
                                setIsRetryingProcess(false);
                            }
                        }}
                    >
                        {isRetryingProcess ? (
                            <><Spinner className="inline-block mr-1 h-3 w-3" />Reiniciando</>
                        ) : (
                            <><RotateCcw className="inline-block mr-1 h-3 w-3" />Reintentar</>
                        )}
                    </Button>
                </ItemActions>
                )}
            </Item>
        </div>
        </motion.div>
        )}
        {hasResumeErrors && (
        <motion.div
            key="resume-errors-alert"
            layout
            initial={{ opacity: 0, height: 0, y: -8 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -8 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            className="w-full overflow-hidden"
        >
        <div className="w-full flex flex-col gap-2 justify-center items-center text-xs">
            {Object.entries(props.resumeErrorsByStore).map(([storeIdKey, storeError]) => {
                const storeId = Number(storeIdKey);
                const storeName = getStoreBdData(storeId, props.storeData)?.name ?? `Tienda ${storeId}`;
                const isRetrying = props.resumingStores.has(storeId);

                return (
                    <Item key={storeId} variant="default" className="bg-red-50 border border-red-200">
                        <ItemMedia variant="icon">
                            <TriangleAlert className="text-red-600" />
                        </ItemMedia>
                        <ItemContent>
                            <ItemTitle className="text-xs text-red-900">{storeName}: reanudación fallida</ItemTitle>
                            <ItemDescription className="text-xs text-red-800">{storeError}</ItemDescription>
                        </ItemContent>
                        <ItemActions>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={isRetrying}
                                onClick={() => props.onRetryResume(storeId)}
                            >
                                {isRetrying ? (
                                    <><Spinner className="inline-block mr-1 h-3 w-3" />Reintentando</>
                                ) : (
                                    <><RotateCcw className="inline-block mr-1 h-3 w-3" />Reintentar</>
                                )}
                            </Button>
                        </ItemActions>
                    </Item>
                );
            })}
        </div>
        </motion.div>
        )}
        {showPartialItem && (
        <motion.div
            key="partial-item-alert"
            layout
            initial={{ opacity: 0, height: 0, y: -8 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -8 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            className="w-full overflow-hidden"
        >
        <div className="w-full flex flex-col gap-2 justify-center items-center text-xs">
            <Item variant="default" className="bg-yellow-50">
                <ItemMedia variant="icon">
                    <TriangleAlert />
                </ItemMedia>
                <ItemContent>
                    <ItemTitle  className="text-xs" >{getNonStockProductsCount(props.status)} productos necesitan reemplazo</ItemTitle>
                    <ItemDescription className="text-xs">
                    {showPartialItem?
                        "Escoge alternativas para completar tus carritos":message
                    }   
                    </ItemDescription>
                </ItemContent>
            {showPartialItem && (
                <ItemActions>
                    <Button size="sm" variant="default" onClick={() => setOpenMissedItemsDialog(true)}>
                        Resolver
                    </Button>
                </ItemActions>
            )}                                
            </Item>
        </div>
        </motion.div>
        )}
        </AnimatePresence>
    </div>
    )
}